<script>
import moment from 'moment-timezone';
import axios from 'axios';
import { formatDateTimeWithFormat } from '@/Modules/Dates.js';
import { localPhoneDigits } from '@/Modules/Phones.js';

export default {
  name: 'InboundCallerID',
  props: {
    provider: {
      type: String,
      default: 'inbound',
    },
  },
  data() {
    return {
      shop_id: this.$page?.props?.master_config?.shop_id,
      time_zone: this.$page?.props?.master_config?.time_zone,
      expiration:
        this.$page?.props?.master_config?.integration?.inbound_event_subscription_expiration ||
        null,
      events: [],
      notifications: [],
      customersCache: {},
    };
  },
  computed: {},
  methods: {
    formatDateTimeWithFormat,
    normalizeCallerNumber(rawNumber) {
      return localPhoneDigits(rawNumber, this.$page?.props?.master_config?.address?.country);
    },
    isExpired() {
      const v = this.expiration;
      if (!v) return true;
      const tz = this.time_zone || 'UTC';
      // Parse server format in shop timezone, fallback if needed
      let exp = moment.tz(v, 'YYYY-MM-DD HH:mm:ss', true, tz);
      if (!exp.isValid()) exp = moment.tz(v, tz);
      if (!exp.isValid()) return true;
      const nowTz = moment.tz(tz);
      return exp.isSameOrBefore(nowTz);
    },
    async subscribeEvents() {
      try {
        const { data } = await axios.get(
          route('inbound-phone-call.inbound.event.subscription', {
            shop_id: this.shop_id,
          }),
        );
        console.log('Subscribed to inbound call events');
      } catch (e) {
        console.error('subscribeEvents error', e);
        throw e;
      }
    },

    subscribePusherChannel() {
      try {
        const channelName = `inbound.call.${this.shop_id}`;
        if (!window?.Echo) {
          console.warn('Echo not initialized; cannot subscribe to', channelName);
          return;
        }
        window.Echo.private(channelName).listen('InboundCallEvent', (payload) => {
          // Keep last 20 events in memory for debugging/usage
          const evt = { ...payload, _received_at: new Date().toISOString() };
          this.events.unshift(evt);
          if (this.events.length > 20) this.events.pop();
          // Drive UI notifications
          this.handleInboundEvent(payload);
        });
        console.log('Listening on', channelName);
      } catch (e) {
        console.error('subscribePusherChannel error', e);
      }
    },

    async handleInboundEvent(payload) {
      try {
        const status = (payload?.status || '').toLowerCase();
        const rawNumber = String(payload?.from || '');
        if (!rawNumber) return;
        const phone = this.normalizeCallerNumber(rawNumber);

        if (status === 'ringing') {
          const customer = await this.getCustomerDetails(phone, true);
          const displayName = customer?.details?.firstname
            ? `${customer.details.firstname} ${customer.details.lastname}`
            : 'Unknown Customer';

          // prevent duplicate ringing for same number
          const exists = this.notifications.some(
            (n) => n.event === 'Incoming Call' && n.callerNumber === phone,
          );
          if (!exists) {
            this.notifications.unshift({
              id: `${phone}-${Date.now()}`,
              event: 'Incoming Call',
              callerNumber: phone,
              callerName: displayName,
              summary: customer?.summary,
              timestamp: new Date().toLocaleTimeString(),
              territory_id: payload?.inbound_territory_id || payload?.territory || null,
              domain: payload?.inbound_domain || payload?.domain || null,
            });
          }
        } else if (status === 'answered') {
          // optional future state; close incoming and show answered with countdown
          this.notifications = this.notifications.filter(
            (n) => !(n.event === 'Incoming Call' && n.callerNumber === phone),
          );
          const customer = await this.getCustomerDetails(phone, true);
          const displayName = customer?.details?.firstname
            ? `${customer.details.firstname} ${customer.details.lastname}`
            : 'Unknown Customer';
          const notif = {
            id: `${phone}-${Date.now()}`,
            event: 'Call Answered',
            callerNumber: phone,
            callerName: displayName,
            summary: customer?.summary,
            timestamp: new Date().toLocaleTimeString(),
            remaining: 20,
            territory_id: payload?.inbound_territory_id || payload?.territory || null,
            domain: payload?.inbound_domain || payload?.domain || null,
          };
          this.notifications.unshift(notif);
          this.$nextTick(() => this.startCountdown(notif));
        } else if (status === 'disconnected') {
          // remove any Incoming Call entries for this number
          this.notifications = this.notifications.filter(
            (n) => !(n.event === 'Incoming Call' && n.callerNumber === phone),
          );
        }
      } catch (e) {
        console.error('handleInboundEvent error', e);
      }
    },

    async getCustomerDetails(phone_number, refresh = false) {
      if (!refresh && this.customersCache[phone_number]) return this.customersCache[phone_number];
      try {
        const { data } = await axios.get(
          route('inbound-phone-call.details', {
            shop_id: this.shop_id,
            phone_number,
          }),
        );
        const value = {
          details: data?.data?.customer,
          summary: data?.data?.summary,
        };
        this.customersCache[phone_number] = value;
        return value;
      } catch (e) {
        console.error('getCustomerDetails error', e);
        const unknown = { unknown: true };
        this.customersCache[phone_number] = unknown;
        return unknown;
      }
    },

    startCountdown(notification) {
      const interval = setInterval(() => {
        notification.remaining--;
        this.$forceUpdate();
        if (notification.remaining <= 0) {
          clearInterval(interval);
          const idx = this.notifications.findIndex((n) => n.id === notification.id);
          if (idx !== -1) this.notifications.splice(idx, 1);
        }
      }, 1000);
    },
    openNotificationDvi(notification) {
      if (!notification?.summary?.dvi_url) return;
      window.open(notification.summary.dvi_url, '_blank', 'noopener');
    },
    hasActiveTicketSummary(summary) {
      return !!(summary?.has_active_ticket || summary?.current_status_name);
    },
  },
  mounted() {
    if (this.provider === 'inbound') {
      if (!this.expiration || this.isExpired()) this.subscribeEvents();
      else console.log('Inbound call event subscription valid until ' + this.expiration);
    }
    // Always attach to pusher channel for real-time events
    this.subscribePusherChannel();
  },
};
</script>

<template>
  <div class="caller-id" v-if="notifications.length">
    <ul>
      <li
        class="caller-id-event"
        v-for="(notif, idx) in notifications"
        :key="notif.id"
        :class="{ 'caller-id-event--link': notif.summary?.dvi_url }"
        :role="notif.summary?.dvi_url ? 'link' : null"
        :tabindex="notif.summary?.dvi_url ? 0 : null"
        @click="openNotificationDvi(notif)"
        @keydown.enter="openNotificationDvi(notif)"
      >
        <i
          class="fa fa-times"
          aria-hidden="true"
          @click.stop="notifications.splice(notifications.indexOf(notif), 1)"
        ></i>
        <strong>
          <i
            class="fas fa-phone"
            aria-hidden="true"
            :class="{ buzz: notif.event === 'Incoming Call' }"
          ></i>
          {{ notif.event }}
        </strong>
        <span v-if="notif.callerName"> : {{ notif.callerName }} ({{ notif.callerNumber }}) </span>
        <span v-else-if="notif.callerNumber"> : {{ notif.callerNumber }} </span>
        <small v-if="notif.timestamp"> ({{ notif.timestamp }})</small>

        <div v-if="notif.remaining !== undefined" style="padding-left: 15px; color: red">
          Closing in {{ notif.remaining }}s
        </div>

        <div v-if="idx === 0 && notif.summary" style="padding-left: 15px">
          <strong>Summary:</strong>
          <ul class="summary">
            <li v-if="hasActiveTicketSummary(notif.summary) && notif.summary.current_status_name">
              <strong>Current Status:</strong>
              {{ notif.summary.current_status_name }}
            </li>
            <li
              v-if="hasActiveTicketSummary(notif.summary) && notif.summary.current_status_vehicle"
            >
              <strong>Current Visit Vehicle:</strong>
              {{ notif.summary.current_status_vehicle }}
            </li>
            <li v-if="notif.summary.dvi_url">
              <strong>DVI:</strong>&nbsp;
              <a :href="notif.summary.dvi_url" target="_blank" rel="noopener" @click.stop>
                <template v-if="notif.summary.dvi_completed_at">
                  Completed on
                  {{
                    formatDateTimeWithFormat(
                      notif.summary.dvi_completed_at,
                      time_zone,
                      'MM/DD/YYYY hh:mm a',
                    )
                  }}
                </template>
                <template v-else>
                  {{ notif.summary.dvi_label || 'View DVI' }}
                </template>
                <i class="fa fa-external-link caller-id-external-link" aria-hidden="true"></i>
              </a>
            </li>
            <li v-if="notif.summary.wo_url">
              <strong>WO:</strong>&nbsp;
              <a :href="notif.summary.wo_url" target="_blank" rel="noopener" @click.stop>
                <template v-if="notif.summary.wo_completed_at">
                  Completed on
                  {{
                    formatDateTimeWithFormat(
                      notif.summary.wo_completed_at,
                      time_zone,
                      'MM/DD/YYYY hh:mm a',
                    )
                  }}
                </template>
                <template v-else-if="notif.summary.wo_generated_at">
                  {{ notif.summary.wo_status }} on
                  {{
                    formatDateTimeWithFormat(
                      notif.summary.wo_generated_at,
                      time_zone,
                      'MM/DD/YYYY hh:mm a',
                    )
                  }}
                </template>
                <template v-else>
                  {{ notif.summary.wo_status || 'View WO' }}
                </template>
                <i class="fa fa-external-link caller-id-external-link" aria-hidden="true"></i>
              </a>
            </li>
            <li v-if="hasActiveTicketSummary(notif.summary) && notif.summary.current_status_date">
              <strong>Last Update:</strong>
              {{
                formatDateTimeWithFormat(
                  notif.summary.current_status_date,
                  time_zone,
                  'MM/DD/YYYY hh:mm a',
                )
              }}
            </li>
            <template v-for="(value, key) in notif.summary" :key="key">
              <li
                v-if="!hasActiveTicketSummary(notif.summary) && key === 'last_visit_date' && value"
              >
                <strong>Last Visit:</strong>
                {{ formatDateTimeWithFormat(value, time_zone, 'MM/DD/YYYY hh:mm a') }}
              </li>
              <li
                v-else-if="
                  !hasActiveTicketSummary(notif.summary) && key === 'last_visit_vehicle' && value
                "
              >
                <strong>Last Visit Vehicle:</strong>
                {{ value }}
              </li>
              <li v-else-if="key === 'last_visits_count' && value > 0">
                <strong>Visit Count:</strong>
                {{ value }}
              </li>
              <li v-else-if="key === 'total_spend' && notif.summary.total_spend">
                <strong>Total Spend:</strong>
                ${{ value }}
              </li>
              <li v-else-if="key === 'nps_score' && value > 0">
                <strong>NPS Score:</strong>
                {{ value }}
              </li>
            </template>
          </ul>
        </div>
      </li>
    </ul>
  </div>
  <div style="display: none"></div>
</template>
